import React from 'react';
import Table, { ColumnConfig } from 'components/table';
import { Icon } from 'components/ui/icon';
import TipsAccordion from 'components/summary-tab/accordions/tips';
import * as util from 'utils/dashboard'; // skipcq: JS-C1003
import * as utils from 'utils/summary'; // skipcq: JS-C1003
import CURRENCY from 'utils/currency';
import { formatDate } from 'utils/date-format';
import { SUMMARY } from 'state/summary/type';
import { PAYMENTS } from 'state/payment/type';
import TotalTipSummary from './TotalTipSummary';

interface TipSummaryByPaymentTypeProps {
  summaryIntervalData: PAYMENTS.TipByPaymentTypeAggregateItem[];
  summaryTotalData: PAYMENTS.ISummaryData;
  activeFreq: SUMMARY.IIntervalType;
  comparisonDate: [string, string];
}

interface TipTotalsRow extends PAYMENTS.TipByPaymentTypeAggregateItem {
  tipAmount: number;
  numberOfTips: number;
  averageTipAmount: number;
}

const getIntervalLabel = (freq: SUMMARY.IIntervalType): string => {
  switch (freq) {
    case SUMMARY.IIntervalType.DAILY:
      return 'Day';
    case SUMMARY.IIntervalType.WEEKLY:
      return 'Week';
    case SUMMARY.IIntervalType.MONTHLY:
      return 'Month';
    default:
      return 'Hour';
  }
};

const TipSummaryByPaymentType = ({
  summaryIntervalData,
  summaryTotalData,
  activeFreq,
  comparisonDate,
}: TipSummaryByPaymentTypeProps): JSX.Element => {
  const intervalLabel = getIntervalLabel(activeFreq);

  const tableData: TipTotalsRow[] = summaryIntervalData.map((row) => ({
    ...row,
    value: { ...row.value },
    tip_breakup: row.tip_breakup,
    tipAmount: row.value.tip_amount,
    numberOfTips: row.value.number_of_tips,
    averageTipAmount: row.value.average_tip_amount,
  }));

  const columns: ColumnConfig<TipTotalsRow>[] = [
    {
      key: 'date',
      label: `Tips by "${intervalLabel}"`,
      renderCell: (row) =>
        row.date ? (
          <div>
            <span className="text-sm text-gray-blue-70">
              {activeFreq === SUMMARY.IIntervalType.DAILY
                ? `on ${formatDate(row.date, 'dd MMM, yyyy')}`
                : activeFreq === SUMMARY.IIntervalType.WEEKLY
                ? `from (${util.getTooltipWeekRange(
                    new Date(row.date),
                    'dd MMM, yyyy'
                  )})`
                : `from (${utils.getMonthlyRange(new Date(row.date))})`}
            </span>
          </div>
        ) : null,
    },
    {
      key: 'tipAmount',
      label: '',
      renderCell: (row) => (
        <div className="text-left text-sm font-medium text-gray-blue-80">
          {row.tipAmount ? CURRENCY.convertToMainUnit(row.tipAmount) : '$0.00'}
        </div>
      ),
    },
    {
      key: 'numberOfTips',
      label: '',
    },
    {
      key: 'averageTipAmount',
      label: '',
      renderCell: (row) => (
        <div className="text-left text-sm font-medium text-gray-blue-80">
          {row.averageTipAmount
            ? CURRENCY.convertToMainUnit(row.averageTipAmount)
            : '$0.00'}
        </div>
      ),
    },
  ];

  const expandRowRenderer = (row: TipTotalsRow) => (
    <div className="w-full pl-0">
      <TipsAccordion row={row} />
    </div>
  );

  return (
    <div>
      <div className="mt-3">
        <div className="mb-3">
          <TotalTipSummary
            summary_data={summaryTotalData}
            previousStartDate={comparisonDate[0]}
            previousEndDate={comparisonDate[1]}
          />
        </div>
        <Table
          data={tableData}
          columns={columns}
          theme={{
            showTableBorder: true,
            tableGridLayout: 'auto 1.51fr 1fr 1fr 1fr',
          }}
          customTheme={{
            HeaderCell: `
              height: 64px;
              background-color: #fff !important;
              font-size: 16px !important;
              line-height: 24px !important;
              text-transform: none !important;
              letter-spacing: 0px !important;
            `,
            Cell: `height: 52px;`,
            Table: `border-radius: 4px; margin-top: 12px;`,
            Row: `
              & > td:first-child {
                border-bottom-left-radius: 4px;
              }
              & > td:last-child {
                border-bottom-right-radius: 4px;
              }
            `,
          }}
          rowExpand={{
            renderer: expandRowRenderer,
            className: 'dropdownArea',
            position: 'left',
            columnRenderer: (expanded: boolean) => (
              <span className="flex items-center justify-center">
                <Icon
                  name={expanded ? 'chevron-down' : 'chevron-right'}
                  size={16}
                  aria-hidden="true"
                />
              </span>
            ),
          }}
        />
      </div>
    </div>
  );
};

export default TipSummaryByPaymentType;
